import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import React from "react";
import {useSelector} from "react-redux";
import {RootState} from "@/store";
import {RouteConstant} from "@/util/constant/RouteConstant.ts";
import {LoginResponse} from "@/model/response/authentication/LoginResponse.tsx";
import {checkAuthentication, routes, RouteType} from "@/router/route";
import {RenderModalComponents} from "@/components/modal/RenderModalComponent.tsx";

type RouteWrapperProps = {
    isAuthenticated: boolean;
    children: React.ReactNode;
    redirectPath?: string;
    userDetail?: LoginResponse;
}

const routeToRouteToWhenNotAuthenticated = RouteConstant.auth.home.path;
const routeToRouteToWhenAuthenticated = RouteConstant.dashboard.dashboard.path

const RouteWrapper = ({isAuthenticated, children, redirectPath}: RouteWrapperProps) => {
    const location = useLocation();
    const from = location.state?.from?.pathname ?? routeToRouteToWhenAuthenticated;

    if (redirectPath && !isAuthenticated) {
        return <Navigate to={redirectPath} replace state={{from: location}}/>;
    }

    if (!redirectPath && isAuthenticated) {
        return <Navigate to={from} replace/>;
    }

    return <>{children}</>;
};

const AppRoutes = () => {
    const authState = useSelector((state: RootState) => state.auth);
    const isAuthenticated = !!authState?.token;
    const location = useLocation();

    const renderComponent = (route: RouteType) => {
        const {metadata, component: Component} = route;
        return metadata.hasSidebar ? (
                <Component/>
        ) : (
            <Component/>
        );
    };

    const getRouteWrapper = (route: RouteType) => {
        const {metadata} = route;
        const shouldAuthenticate = checkAuthentication(metadata.isAuthenticated, isAuthenticated);
        if (metadata.isAuthenticated === false) {
            return (
                <RouteWrapper
                    userDetail={authState?.userInfo}
                    isAuthenticated={isAuthenticated}
                    redirectPath={undefined}
                >
                    {renderComponent(route)}
                </RouteWrapper>
            );
        }

        return (
            <RouteWrapper
                isAuthenticated={shouldAuthenticate}
                redirectPath={routeToRouteToWhenNotAuthenticated}
            >
                {renderComponent(route)}
            </RouteWrapper>
        );
    };

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <Navigate
                        to={isAuthenticated ? routeToRouteToWhenAuthenticated : routeToRouteToWhenNotAuthenticated}
                        // to={ routeToRouteToWhenAuthenticated}
                        replace
                    />
                }
            />
            {routes.map((route, index) => (
                <Route
                    key={index}
                    path={route.path}
                    element={
                        route.metadata.redirectTo ?
                            <Navigate to={route.metadata.redirectTo} replace/> :
                            getRouteWrapper(route)
                    }
                />
            ))}
            <Route
                path="/file/*"
                element={null}
            />
            <Route
                path="*"
                element={
                    <Navigate
                        to={RouteConstant.auth.error.path}
                        replace
                        state={{from: location}}
                    />
                }
            />
        </Routes>
    );
};

export const AppRouter = () => (
    <>
        <AppRoutes/>
        <RenderModalComponents/>
    </>
);